import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateGroupMutation, useUpdateGroupMutation, useAcceptInvitationMutation, useRejectInvitationMutation, useGroupsQuery, useGroupMembersQuery } from '../hooks/queries/useGroupQueries';
import * as groupService from '../services/group/groupService';
import type { Group, GroupInvitation, GroupMemberRole } from '@ext-bookmark/shared/types';
import { groupSchema, inviteMemberSchema, type GroupFormData, type InviteMemberFormData } from '../lib/validation-schemas';
import { FormError } from './ui/form-error';
import AuthModal from './AuthModal';
import MemberCard from './MemberCard';
import InvitationCard from './InvitationCard';

interface GroupManagementProps {
  onClose: () => void;
  onGroupChange?: (groupId: string | null) => void;
}

const GroupManagement: React.FC<GroupManagementProps> = ({ onClose, onGroupChange }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const createGroupMutation = useCreateGroupMutation();
  const updateGroupMutation = useUpdateGroupMutation();
  const acceptInvitationMutation = useAcceptInvitationMutation();
  const rejectInvitationMutation = useRejectInvitationMutation();
  const { data: groups = [], isLoading: groupsLoading } = useGroupsQuery({ enabled: !!user });
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const { data: members = [] } = useGroupMembersQuery(selectedGroup?.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showEditNameForm, setShowEditNameForm] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [invitations, setInvitations] = useState<GroupInvitation[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<GroupMemberRole>('member');
  const [_updating, setUpdating] = useState(false);

  // 创建分组表单
  const {
    register: registerCreateGroup,
    handleSubmit: handleCreateGroupSubmit,
    formState: { errors: createGroupErrors, isSubmitting: isCreating },
    reset: resetCreateGroupForm,
  } = useForm<GroupFormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: { name: '' },
  });

  // 编辑分组名称表单
  const {
    register: registerEditGroup,
    handleSubmit: handleEditGroupSubmit,
    formState: { errors: editGroupErrors, isSubmitting: isUpdating },
    reset: resetEditGroupForm,
  } = useForm<GroupFormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: { name: '' },
  });

  // 邀请成员表单
  const {
    register: registerInvite,
    handleSubmit: handleInviteSubmit,
    formState: { errors: inviteErrors, isSubmitting: isInviting },
    reset: resetInviteForm,
  } = useForm<InviteMemberFormData>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { email: '' },
  });
  
  // 预设颜色列表
  const presetColors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#84CC16', // lime
    '#F97316', // orange
    '#6366F1', // indigo
  ];

  useEffect(() => {
    if (user) setShowAuthModal(false);
    else setShowAuthModal(true);
  }, [user]);

  // 从 React Query 的 members 派生当前用户角色
  useEffect(() => {
    if (!user || members.length === 0) return;
    const currentMember = members.find(m => m.userId === user.id);
    if (currentMember) setCurrentUserRole(currentMember.role);
  }, [user, members]);

  // 分组列表更新后同步 selectedGroup（名称/颜色等）
  useEffect(() => {
    if (!selectedGroup || groups.length === 0) return;
    const updated = groups.find(g => g.id === selectedGroup.id);
    if (updated && (updated.name !== selectedGroup.name || updated.color !== selectedGroup.color)) {
      setSelectedGroup(updated);
    }
  }, [groups, selectedGroup]);

  // 加载选中分组的邀请列表（无 React Query hook，仍用 service）
  useEffect(() => {
    if (selectedGroup) {
      loadInvitations(selectedGroup.id);
      resetEditGroupForm({ name: selectedGroup.name });
    }
  }, [selectedGroup, resetEditGroupForm]);

  const loadInvitations = async (groupId: string) => {
    const { invitations: invitationList, error: err } = await groupService.getGroupInvitations(groupId);
    if (err) {
      console.error('[GroupManagement] 加载邀请列表失败:', err);
    } else {
      setInvitations(invitationList);
    }
  };

  const onCreateGroup = async (data: GroupFormData) => {
    setError(null);
    try {
      const group = await createGroupMutation.mutateAsync(data.name.trim());
      resetCreateGroupForm();
      setShowCreateForm(false);
      if (group) {
        setSelectedGroup(group);
        if (onGroupChange) onGroupChange(group.id);
      }
    } catch (err: any) {
      setError(err?.message || '创建失败，请重试');
    }
  };

  const onUpdateGroupName = async (data: GroupFormData) => {
    if (!selectedGroup) return;

    // 检查权限
    const { hasPermission } = await groupService.checkPermission(selectedGroup.id, 'update');
    if (!hasPermission) {
      setError('只有创建者和管理员可以修改分组名称');
      return;
    }

    setError(null);
    try {
      await updateGroupMutation.mutateAsync({ groupId: selectedGroup.id, name: data.name.trim() });
      setShowEditNameForm(false);
      setSelectedGroup(prev => prev ? { ...prev, name: data.name.trim() } : null);
    } catch (err: any) {
      setError(err?.message || '更新失败，请重试');
    }
  };

  const handleUpdateGroupColor = async (color: string | null) => {
    if (!selectedGroup) return;

    // 检查权限
    const { hasPermission } = await groupService.checkPermission(selectedGroup.id, 'update');
    if (!hasPermission) {
      setError('只有创建者和管理员可以修改分组颜色');
      return;
    }

    setUpdating(true);
    setError(null);
    try {
      await updateGroupMutation.mutateAsync({ groupId: selectedGroup.id, color });
      setShowColorPicker(false);
      setSelectedGroup(prev => prev ? { ...prev, color: color ?? undefined } : null);
      onGroupChange?.(selectedGroup.id);
    } catch (err: any) {
      setError(err?.message || '更新颜色失败');
    } finally {
      setUpdating(false);
    }
  };

  const onInviteMember = async (data: InviteMemberFormData) => {
    if (!selectedGroup) {
      setError('请先选择分组');
      return;
    }

    setError(null);
    try {
      const { invitation, error: err } = await groupService.inviteMember(selectedGroup.id, data.email.trim());
      if (err) {
        setError(err.message);
      } else {
        resetInviteForm();
        setShowInviteForm(false);
        await loadInvitations(selectedGroup.id);
        if (invitation) {
          setError(null);
        }
      }
    } catch (err: any) {
      setError(err.message || '邀请失败，请重试');
    }
  };

  const _handleAcceptInvitation = async (invitationId: string) => {
    if (!selectedGroup) return;

    setLoading(true);
    setError(null);
    try {
      await acceptInvitationMutation.mutateAsync(invitationId);
      queryClient.invalidateQueries({ queryKey: ['groups', selectedGroup.id, 'members'] });
      await loadInvitations(selectedGroup.id);
    } catch (err: any) {
      setError(err?.message || '接受邀请失败');
    } finally {
      setLoading(false);
    }
  };

  const _handleRejectInvitation = async (invitationId: string) => {
    if (!selectedGroup) return;

    setLoading(true);
    setError(null);
    try {
      await rejectInvitationMutation.mutateAsync(invitationId);
      queryClient.invalidateQueries({ queryKey: ['groups', selectedGroup.id, 'members'] });
      await loadInvitations(selectedGroup.id);
    } catch (err: any) {
      setError(err?.message || '拒绝邀请失败');
    } finally {
      setLoading(false);
    }
  };
  void _handleAcceptInvitation;
  void _handleRejectInvitation;

  const handleUpdateMemberRole = async (userId: string, newRole: GroupMemberRole) => {
    if (!selectedGroup) return;

    setUpdating(true);
    setError(null);
    const { error: err } = await groupService.updateMemberRole(selectedGroup.id, userId, newRole);
    if (err) {
      setError(err.message);
    } else {
      queryClient.invalidateQueries({ queryKey: ['groups', selectedGroup.id, 'members'] });
    }
    setUpdating(false);
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;
    if (!confirm('确定要移除该成员吗？')) return;

    setLoading(true);
    setError(null);
    const { error: err } = await groupService.removeMember(selectedGroup.id, userId);
    if (err) {
      setError(err.message);
    } else {
      queryClient.invalidateQueries({ queryKey: ['groups', selectedGroup.id, 'members'] });
    }
    setLoading(false);
  };

  const handleLeaveGroup = async () => {
    if (!selectedGroup) return;
    if (!confirm('确定要离开该分组吗？')) return;

    setLoading(true);
    setError(null);
    const { error: err } = await groupService.leaveGroup(selectedGroup.id);
    if (err) {
      setError(err.message);
    } else {
      setSelectedGroup(null);
      if (onGroupChange) onGroupChange(null);
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    }
    setLoading(false);
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    if (!confirm('确定要删除该分组吗？此操作不可恢复！')) return;

    // 检查权限
    const { hasPermission } = await groupService.checkPermission(selectedGroup.id, 'delete');
    if (!hasPermission) {
      setError('只有创建者可以删除分组');
      return;
    }

    setLoading(true);
    setError(null);
    const { error: err } = await groupService.deleteGroup(selectedGroup.id);
    if (err) {
      setError(err.message);
    } else {
      setSelectedGroup(null);
      if (onGroupChange) onGroupChange(null);
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    }
    setLoading(false);
  };

  // 如果未登录，显示登录弹窗
  if (!user) {
    return (
      <>
        <div className="p-4">
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              请先登录以使用分组管理功能
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              登录后可以在多设备间同步数据
            </p>
          </div>
        </div>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => {
              setShowAuthModal(false);
              // 如果用户关闭登录弹窗但未登录，关闭整个分组管理界面
              if (!user) {
                onClose();
              }
            }}
          />
        )}
      </>
    );
  }

  const _personalGroup = groups.find(g => g.isPersonal);
  void _personalGroup;

  return (
    <div className="max-w-2xl mx-auto">

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* 创建分组表单 */}
      {showCreateForm ? (
        <form onSubmit={handleCreateGroupSubmit(onCreateGroup)} className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="text-lg font-medium mb-3 text-gray-900 dark:text-gray-100">创建新分组</h3>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                {...registerCreateGroup('name')}
                placeholder="输入分组名称"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={isCreating}
              />
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:bg-blue-700"
              >
                {isCreating ? '创建中...' : '创建'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetCreateGroupForm();
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                取消
              </button>
            </div>
            <FormError error={createGroupErrors.name?.message} />
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowCreateForm(true)}
          className="mb-4 w-full px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          创建新分组
        </button>
      )}

      {/* 分组列表 */}
      {groupsLoading && groups.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">加载中...</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>还没有分组</p>
          <p className="text-sm mt-2">创建一个分组开始协作吧！</p>
        </div>
      ) : (
        <div className="space-y-2 mb-4">
          {groups.map((group) => (
            <div
              key={group.id}
              onClick={() => setSelectedGroup(group)}
              className={`p-3 rounded-lg cursor-pointer transition-colors ${
                selectedGroup?.id === group.id
                  ? 'bg-ios-blue text-white'
                  : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {group.name}
                    {group.isPersonal && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded">
                        个人分组
                      </span>
                    )}
                  </div>
                  <div className={`text-sm ${selectedGroup?.id === group.id ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                    创建于 {new Date(group.createdAt).toLocaleDateString('zh-CN')}
                  </div>
                </div>
                {selectedGroup?.id === group.id && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 分组成员列表 */}
      {selectedGroup && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {selectedGroup.name} - 成员列表
            </h3>
            <div className="flex gap-2">
              {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
                <>
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="px-3 py-1 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    title="设置颜色"
                  >
                    颜色
                  </button>
                  {selectedGroup.isPersonal && (
                    <button
                      onClick={() => setShowEditNameForm(!showEditNameForm)}
                      className="px-3 py-1 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      {showEditNameForm ? '取消' : '重命名'}
                    </button>
                  )}
                </>
              )}
              {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
                <button
                  onClick={() => setShowInviteForm(!showInviteForm)}
                  className="px-3 py-1 text-sm bg-ios-blue text-white rounded-lg hover:bg-blue-600"
                >
                  {showInviteForm ? '取消' : '邀请成员'}
                </button>
              )}
            </div>
          </div>

          {/* 颜色选择器 */}
          {showColorPicker && (
            <div className="mb-4 p-3 bg-white dark:bg-gray-700 rounded-lg">
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">选择颜色</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleUpdateGroupColor(null)}
                  className={`w-8 h-8 rounded border-2 ${
                    !selectedGroup.color
                      ? 'border-gray-900 dark:border-gray-100'
                      : 'border-gray-300 dark:border-gray-600'
                  } bg-gray-200 dark:bg-gray-600 flex items-center justify-center`}
                  title="默认颜色"
                >
                  {!selectedGroup.color && (
                    <svg className="w-4 h-4 text-gray-900 dark:text-gray-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleUpdateGroupColor(color)}
                    className={`w-8 h-8 rounded border-2 ${
                      selectedGroup.color === color
                        ? 'border-gray-900 dark:border-gray-100'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {selectedGroup.color === color && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 重命名表单（仅个人分组） */}
          {showEditNameForm && selectedGroup.isPersonal && (
            <form onSubmit={handleEditGroupSubmit(onUpdateGroupName)} className="mb-4 p-3 bg-white dark:bg-gray-700 rounded-lg">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    {...registerEditGroup('name')}
                    placeholder="输入分组名称"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={isUpdating}
                  />
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isUpdating ? '保存中...' : '保存'}
                  </button>
                </div>
                <FormError error={editGroupErrors.name?.message} />
              </div>
            </form>
          )}

          {/* 邀请表单 */}
          {showInviteForm && (
            <form onSubmit={handleInviteSubmit(onInviteMember)} className="mb-4 p-3 bg-white dark:bg-gray-700 rounded-lg">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    {...registerInvite('email')}
                    placeholder="输入成员邮箱"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={isInviting}
                  />
                  <button
                    type="submit"
                    disabled={isInviting}
                    className="px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isInviting ? '邀请中...' : '邀请'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false);
                      resetInviteForm();
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    取消
                  </button>
                </div>
                <FormError error={inviteErrors.email?.message} />
              </div>
            </form>
          )}

          {/* 成员列表 */}
          <div className="space-y-2 mb-4">
            {members.length === 0 ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                暂无成员
              </div>
            ) : (
              members.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  currentUserRole={currentUserRole}
                  currentUserId={user?.id || ''}
                  onRoleChange={handleUpdateMemberRole}
                  onRemove={handleRemoveMember}
                />
              ))
            )}
          </div>

          {/* 邀请列表 */}
          {invitations.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                📧 邀请记录
              </h4>
              <div className="space-y-2">
                {invitations.map((invitation) => (
                  <InvitationCard
                    key={invitation.id}
                    invitation={invitation}
                    showActions={false} // 在分组管理页面不显示操作按钮，邀请处理在通知中心进行
                  />
                ))}
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          {!selectedGroup.isPersonal && (
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleLeaveGroup}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                离开分组
              </button>
              {currentUserRole === 'owner' && (
                <button
                  onClick={handleDeleteGroup}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                >
                  删除分组
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
